import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, defer, Observable, of, throwError } from 'rxjs';
import { Configuration, AuthenticationControllerService, EmployeeControllerService } from '@set-it-workflow/set-it-workflow-ts-angular';
import { map, catchError, finalize, shareReplay } from 'rxjs/operators';
import jwt_decode from 'jwt-decode';
import { environment } from 'src/environments/environment';

/** Path of the token-refresh endpoint, as built by the generated AuthenticationControllerService. */
export const REFRESH_TOKEN_URL = `${environment.apiBasePath}/authentication/refresh-token`;

/**
 * Exact-path match, deliberately not a substring test: a business endpoint whose name merely
 * contains "refresh" must still get token handling and 401 recovery.
 */
export function isRefreshTokenUrl(url: string): boolean {
  return url === REFRESH_TOKEN_URL;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private static readonly EXPIRY_BUFFER_SECONDS = 60;
  private readonly ACCESS_TOKEN = 'set-it-workflow-access';
  private readonly REFRESH_TOKEN = 'set-it-workflow-refresh';
  accessTokenSubject = new BehaviorSubject<string>(null);
  refreshTokenSubject = new BehaviorSubject<string>(null);
  userProfile = new BehaviorSubject<any>(null);

  /** The one in-flight refresh every caller shares; null whenever no refresh is running. */
  private refreshInFlight: Observable<void> = null;

  constructor(
    private apiConfig: Configuration,
    private authenServiceController: AuthenticationControllerService,
    private router: Router,
  ) {

  }
  get token(): string {
    return localStorage.getItem(this.ACCESS_TOKEN);
  }
  get refreshToken(): string {
    return localStorage.getItem(this.REFRESH_TOKEN);
  }
  updateToken(token: string, refreshToken: string): void {
    localStorage.setItem(this.ACCESS_TOKEN, token);
    localStorage.setItem(this.REFRESH_TOKEN, refreshToken);
    this.accessTokenSubject.next(token);
    this.refreshTokenSubject.next(refreshToken);

    this.updateSwaggerToken();
  }
  updateSwaggerToken(): void {
    this.apiConfig.apiKeys.Authorization = 'Bearer ' + this.token;
  }


  /**
   * Drops every trace of the current session. Safe to call when nothing is stored.
   *
   * The api key is reset through updateSwaggerToken() rather than blanked. AuthInterceptor decides
   * whether it owns a request by the presence of an Authorization header, and the generated client
   * only sets that header when the api key is truthy — so blanking it would make every later
   * client call slip past the interceptor and reach the backend with no credentials at all. This
   * leaves the same "Bearer null" value the app already starts up with when no one is logged in.
   */
  clearSession(): void {
    localStorage.removeItem(this.ACCESS_TOKEN);
    localStorage.removeItem(this.REFRESH_TOKEN);
    this.accessTokenSubject.next(null);
    this.refreshTokenSubject.next(null);
    if (this.apiConfig.apiKeys) {
      this.updateSwaggerToken();
    }
  }

  get isAuthenticated(): Observable<boolean> | boolean {
    if (this.token === null || this.token === undefined) {
      return false;
    } else if (this.doesTokenExpired()) {
      if (this.doesRefreshTokenExpired()) {
        return false;
      } else {
        return this.refresh().pipe(
          map(() => true),
          catchError(() => of(false))
        );
      }
    } else {
      return true;
    }
  }
  /**
   * Treats the access token as expired `EXPIRY_BUFFER_SECONDS` early so a request is never sent
   * with a token that dies in flight. The realm lifespan is 5 min (ENS-DEV, verified 2026-07-31).
   *
   * This reads the client clock, so skew larger than the buffer still slips through — the 401
   * refresh-and-retry-once path in AuthInterceptor is what absorbs that.
   */
  doesTokenExpired(): boolean {
    return this.token == null
      ? true
      : (jwt_decode(this.token) as any).exp < Date.now() / 1000 + AuthService.EXPIRY_BUFFER_SECONDS;
  }

  doesRefreshTokenExpired(): boolean {
    return this.refreshToken == null
      ? true
      : (jwt_decode(this.refreshToken) as any).exp < Date.now() / 1000;
  }

  getName(): string {
    return this.token == null ? null : (jwt_decode(this.token) as any).name;
  }
  getEmployeeId():string{
    return this.token == null ? null : (jwt_decode(this.token) as any).employee_id;
  }


  /**
   * Renews the access token. Concurrent callers share one in-flight request and one outcome.
   *
   * Any failure — whatever its shape — clears the session and routes to login exactly once,
   * then errors so no caller is left waiting. The in-flight reference is released in `finalize`,
   * so a failed refresh can never strand later requests.
   */
  refresh(): Observable<void> {
    if (this.refreshInFlight) {
      return this.refreshInFlight;
    }

    // defer() reads the refresh token and starts the request at *subscribe* time, so a caller
    // that subscribes late still sends the current token instead of a snapshot from call time.
    this.refreshInFlight = defer(() =>
      this.authenServiceController.refreshTokenUsingPOST({ refreshToken: this.refreshToken })
    )
      .pipe(
        map((x: any) => {
          this.updateToken(x.access_token, x.refresh_token);
        }),
        catchError((err) => {
          this.clearSession();
          this.router.navigateByUrl('/login');
          return throwError(err);
        }),
        finalize(() => {
          this.refreshInFlight = null;
        }),
        shareReplay({ bufferSize: 1, refCount: false })
      );

    return this.refreshInFlight;
  }






}
